import {
  AVTaxonomyCreateDTO,
  AVTaxonomyDTO,
  GetTaxonomiesResponse,
  LegacyTaxonomyDTO,
} from '@scale/shared/taxonomyService/dto/AVTaxonomyDTO';
import {
  SaveDraftRequestDTO,
  InheritRequestDTO,
  InheritResultDTO,
  ForkRequestDTO,
  BackfillTasksTaxonomyRequestDTO,
  BackfillTasksInstructionsRequestDTO,
  ReviewTaxonomyProposalDTO,
} from '@scale/shared/taxonomyService/dto/actionDtos';
import { ProjectInfoDTO } from '@scale/shared/taxonomyService/dto/ProjectInfoDTO';
import {
  useQuery,
  useMutation,
  MutateOptions,
  QueryObserverResult,
} from '@tanstack/react-query-v4';
import SAlert from 'react-s-alert';
import { invokeAsyncCorpJob } from '@scale/frontend-lib/invokeCorpJob';
import {
  AVInstructionsDTO,
  TaxonomyInstructionsHistoryDTO,
} from '@scale/shared/taxonomyService/dto/AVInstructionsDTO';
import { ComposableInstruction } from '@scale/shared/taxonomyService/dto/ComposableInstruction';
import {
  TaxonomyCache,
  makeTaxonomyCache,
} from 'react-scale/dashboard.scale.com/components/AVTaxonomy/apiClients/TaxonomyCache';
import { taxonomyApiFetch } from 'react-scale/dashboard.scale.com/components/AVTaxonomy/apiClients/apiUtils';
import { TaskStatus } from '@scale/shared/defaults';

export async function deleteTaxonomiesByProjectId(projectId: string) {
  try {
    return await taxonomyApiFetch(`internal/taxonomies?projectId=${projectId}`, {
      method: 'DELETE',
    });
  } catch (err) {
    SAlert.error('Failed to delete taxonomies: ' + err);
  }
}

export async function deleteInstructionsByProjectId(projectId: string) {
  try {
    return await taxonomyApiFetch(`internal/instructions?projectId=${projectId}`, {
      method: 'DELETE',
    });
  } catch (err) {
    SAlert.error('Failed to delete instructions: ' + err);
  }
}

export async function getInstructionsForAssignment(assignmentId: string) {
  try {
    return await taxonomyApiFetch(`internal/instructions/assignment/${assignmentId}`, {
      method: 'GET',
    });
  } catch (err) {
    SAlert.error('Failed to get instructions for assignment: ' + err);
  }
}

export async function checkProjectHasAnyAVTaxonomy(projectId: string) {
  try {
    const { hasAnyTaxonomy } = await taxonomyApiFetch(
      `internal/taxonomies/has_any?projectId=${projectId}`,
      {
        method: 'GET',
      },
    );
    return hasAnyTaxonomy;
  } catch (err) {
    return;
  }
}

export async function getTaxonomyById(taxonomyId: string) {
  try {
    return await taxonomyApiFetch<AVTaxonomyDTO>(`internal/taxonomy?id=${taxonomyId}`, {
      method: 'GET',
    });
  } catch (_err) {
    SAlert.error('Taxonomy not found', { timeout: 'none' });
  }
}

export type FrontendSaveDraftRequest = SaveDraftRequestDTO & {
  taxonomyId: string;
  withPendingApproval?: boolean;
};
type FrontendInheritRequest = InheritRequestDTO & { taxonomyId: string };
export type FrontendForkRequest = ForkRequestDTO & {
  sourceTaxonomyId: string;
  sourceTaxonomyName: string;
};
type FrontendReviewTaxonomyProposalRequest = ReviewTaxonomyProposalDTO & {
  taxonomyId: string;
};

export interface FrontendBackfillRequest {
  taskIds?: string[];
  batchId?: string;
  taskStatus?: TaskStatus[];
  ignoreMatchingTaxonomySrn: boolean;
  ignoreBackfillConditions: boolean;
}

export interface TaxonomyApiClient {
  loadedTaxonomyCache?: TaxonomyCache | null;
  refetchTaxonomiesAndInstructions: () => Promise<QueryObserverResult<TaxonomyCache | null, Error>>;
  isLoadingTaxonomies: boolean;

  createTaxonomy: (
    taxonomyCreate: AVTaxonomyCreateDTO,
    opts?: MutateOptions<AVTaxonomyDTO, Error, AVTaxonomyCreateDTO>,
  ) => void;
  isCreatingTaxonomy: boolean;

  saveDraft: (
    SaveDraftRequestDTO: FrontendSaveDraftRequest & { withPendingApproval?: boolean },
    opts?: MutateOptions<AVTaxonomyDTO, Error, FrontendSaveDraftRequest>,
  ) => void;
  isSavingDraft: boolean;

  saveProposal: (
    SaveDraftRequestDTO: FrontendSaveDraftRequest,
    opts?: MutateOptions<AVTaxonomyDTO, Error, FrontendSaveDraftRequest>,
  ) => void;
  isSavingProposal: boolean;

  inheritTaxonomies: (
    req: FrontendInheritRequest,
    opts?: MutateOptions<InheritResultDTO, Error, FrontendInheritRequest>,
  ) => void;
  isInheriting: boolean;

  publishTaxonomy: (
    req: FrontendInheritRequest & {
      instructionsIdToCopy?: string;
      inheritMasterInstructions?: boolean;
    },
    opts?: MutateOptions<
      InheritResultDTO,
      Error,
      FrontendInheritRequest & {
        instructionsIdToCopy?: string;
        inheritMasterInstructions?: boolean;
      }
    >,
  ) => void;
  isPublishing: boolean;

  requestFEApproval: (
    req: FrontendInheritRequest & { instructionsIdToCopy?: string },
    opts?: MutateOptions<
      InheritResultDTO,
      Error,
      FrontendInheritRequest & { instructionsIdToCopy?: string }
    >,
  ) => void;
  isRequestingApproval: boolean;

  forkTaxonomy: (
    req: FrontendForkRequest,
    opts?: MutateOptions<AVTaxonomyDTO, Error, FrontendForkRequest>,
  ) => void;
  isForking: boolean;

  commitTaxonomy: (
    req: { taxonomyId: string },
    opts?: MutateOptions<void, Error, { taxonomyId: string }>,
  ) => void;
  isCommitting: boolean;

  recallTaxonomy: (
    req: { taxonomyId: string },
    opts?: MutateOptions<void, Error, { taxonomyId: string }>,
  ) => void;
  isRecalling: boolean;

  backfillTasksWithTaxonomy: (
    req: BackfillTasksTaxonomyRequestDTO,
    opts?: MutateOptions<any, Error, BackfillTasksTaxonomyRequestDTO>,
  ) => void;
  isSpawningBackfillJob: boolean;

  updateTaxonomyDescription: (
    req: { taxonomyId: string; description: string },
    opts?: MutateOptions<void, Error, { taxonomyId: string; description: string }>,
  ) => void;
  isUpdatingDescription: boolean;

  updateTaxonomyRootName: (
    req: { taxonomyId: string; rootName: string },
    opts?: MutateOptions<void, Error, { taxonomyId: string; rootName: string }>,
  ) => void;
  isUpdatingRootName: boolean;

  reviewTaxonomyProposal: (
    req: FrontendReviewTaxonomyProposalRequest,
    opts?: MutateOptions<AVTaxonomyDTO, Error, FrontendReviewTaxonomyProposalRequest>,
  ) => void;
  isReviewingProposal: boolean;

  getAllTaxonomyInstructions: (
    req: { taxonomyId: string },
    opts?: MutateOptions<TaxonomyInstructionsHistoryDTO, Error, { taxonomyId: string }>,
  ) => void;
  isGettingAllTaxonomyInstructions: boolean;

  getTaxonomyById: (
    req: { taxonomyId: string },
    opts?: MutateOptions<any, Error, { taxonomyId: string }>,
  ) => void;
  isGettingTaxonomyById: boolean;

  getLatestTaxonomyInstructions: (
    req: { taxonomyId: string },
    opts?: MutateOptions<AVInstructionsDTO | null, Error, { taxonomyId: string }>,
  ) => void;

  getLatestPublishedInstructions: (
    req: { taxonomyId: string },
    opts?: MutateOptions<AVInstructionsDTO | null, Error, { taxonomyId: string }>,
  ) => void;

  createNewTaxonomyInstructions: (
    req: { taxonomyId: string },
    opts?: MutateOptions<AVInstructionsDTO, Error, { taxonomyId: string }>,
  ) => void;
  isCreatingInstructions: boolean;

  getInstructionsById: (
    req: { instructionId: string },
    opts?: MutateOptions<AVInstructionsDTO | null, Error, { instructionId: string }>,
  ) => void;

  saveDraftInstructions: (
    req: { instructionId: string; draft: ComposableInstruction },
    opts?: MutateOptions<
      AVInstructionsDTO,
      Error,
      { instructionId: string; draft: ComposableInstruction }
    >,
  ) => void;
  isSavingDraftInstructions: boolean;

  publishDraftInstructions: (
    req: { instructionId: string },
    opts?: MutateOptions<AVInstructionsDTO, Error, { instructionId: string }>,
  ) => void;
  isPublishingInstructions: boolean;

  backfillTasksWithInstructions: (
    req: BackfillTasksInstructionsRequestDTO,
    opts?: MutateOptions<any, Error, BackfillTasksInstructionsRequestDTO>,
  ) => void;
  isBackfillingInstructions: boolean;

  getMasterTaxonomy: (
    req: { projectId: string },
    opts?: MutateOptions<AVTaxonomyDTO | null, Error, { projectId: string }>,
  ) => void;
  isGettingMasterTaxonomy: boolean;
}

export function useTaxonomyApiClient({
  projectInfo,
  isCorp,
  isEditable,
  customerCanViewInstructions,
  customerCanPublish,
}: {
  projectInfo?: ProjectInfoDTO;
  isCorp: boolean;
  isEditable: boolean;
  customerCanViewInstructions: boolean;
  customerCanPublish: boolean;
}): TaxonomyApiClient {
  const projectId = projectInfo?._id;

  const getTaxonomiesAndInstructionsQuery = useQuery<TaxonomyCache | null, Error>({
    queryKey: ['AVTaxonomy', 'byProject', { projectId }],
    queryFn: async () => {
      if (!projectId) {
        return null;
      }
      const { taxonomies, taxIdToSortedLightInstructions, masterTaxonomy, hasMasterInstructions } =
        await taxonomyApiFetch<GetTaxonomiesResponse>(
          `internal/taxonomies?projectId=${projectId}&withInstructions`,
        );

      const legacyTaxonomies = await taxonomyApiFetch<LegacyTaxonomyDTO[]>(
        `internal/taxonomies/legacy?projectId=${projectId}`,
      );

      return makeTaxonomyCache(
        taxonomies,
        legacyTaxonomies,
        taxIdToSortedLightInstructions ?? {},
        projectInfo,
        {
          isCorp,
          isEditable,
          customerCanViewInstructions,
          customerCanPublish,
        },
        masterTaxonomy,
        hasMasterInstructions,
      );
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const createTaxonomyMutation = useMutation<AVTaxonomyDTO, Error, AVTaxonomyCreateDTO>({
    mutationFn: async taxonomyCreate => {
      const taxonomy: AVTaxonomyDTO = await taxonomyApiFetch('internal/taxonomies', {
        method: 'POST',
        body: taxonomyCreate,
      });
      return taxonomy;
    },
    onSuccess: () => {
      // naive refetch. can move to metadata-only Taxonomy API fetches that are by ID in the future
      // to lower post-update refetch size
      refetchTaxonomiesAndInstructions();
    },
  });

  const saveDraftTaxonomyMutation = useMutation<AVTaxonomyDTO, Error, FrontendSaveDraftRequest>({
    mutationFn: async ({ taxonomyBody, taxonomyId, withPendingApproval }) => {
      const taxonomy: AVTaxonomyDTO = await taxonomyApiFetch(
        `internal/taxonomies/${taxonomyId}/draft`,
        {
          method: 'POST',
          body: { taxonomyBody, withPendingApproval },
        },
      );
      return taxonomy;
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const saveProposalTaxonomyMutation = useMutation<AVTaxonomyDTO, Error, FrontendSaveDraftRequest>({
    mutationFn: async ({ taxonomyBody, taxonomyId }) => {
      const taxonomy: AVTaxonomyDTO = await taxonomyApiFetch(
        `internal/taxonomies/${taxonomyId}/proposal`,
        {
          method: 'POST',
          body: { taxonomyBody },
        },
      );
      return taxonomy;
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const inheritTaxonomiesMutation = useMutation<InheritResultDTO, Error, FrontendInheritRequest>({
    mutationFn: async ({ taxonomyId, inheritTaxonomyIds }) => {
      const result: InheritResultDTO = await taxonomyApiFetch(
        `internal/taxonomies/${taxonomyId}/inherit`,
        {
          method: 'POST',
          body: { inheritTaxonomyIds },
        },
      );
      return result;
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const publishTaxonomyMutation = useMutation<
    InheritResultDTO,
    Error,
    FrontendInheritRequest & { instructionsIdToCopy?: string; inheritMasterInstructions?: boolean }
  >({
    mutationFn: async ({
      taxonomyId,
      inheritTaxonomyIds,
      instructionsIdToCopy,
      inheritMasterInstructions,
    }) => {
      const result: InheritResultDTO = await taxonomyApiFetch(
        `internal/taxonomies/${taxonomyId}/publish`,
        {
          method: 'POST',
          body: { inheritTaxonomyIds, instructionsIdToCopy, inheritMasterInstructions },
        },
      );
      return result;
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const requestFEApprovalMutation = useMutation<
    InheritResultDTO,
    Error,
    FrontendInheritRequest & { instructionsIdToCopy?: string }
  >({
    mutationFn: async ({ taxonomyId, instructionsIdToCopy }) => {
      const result: InheritResultDTO = await taxonomyApiFetch(
        `internal/taxonomies/${taxonomyId}/request_fe_approval`,
        {
          method: 'POST',
          body: { instructionsIdToCopy },
        },
      );
      return result;
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const forkTaxonomyMutation = useMutation<AVTaxonomyDTO, Error, FrontendForkRequest>({
    mutationFn: async ({ sourceTaxonomyId, name, description }) => {
      const result: AVTaxonomyDTO = await taxonomyApiFetch(
        `internal/taxonomies/${sourceTaxonomyId}/fork`,
        {
          method: 'POST',
          body: { name, description },
        },
      );
      return result;
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const useBackfillTasksWithTaxonomy = () =>
    useMutation<any, Error, BackfillTasksTaxonomyRequestDTO>(
      async ({
        projectId,
        projectName,
        taxonomySrn,
        taskIds,
        batchId,
        ignoreMatchingTaxonomySrn,
        taskStatus,
        ignoreBackfillConditions,
      }) => {
        return await invokeAsyncCorpJob('backfillTasksTaxonomy', {
          projectId,
          projectName,
          taxonomySrn,
          taskIds,
          batchId,
          ignoreMatchingTaxonomySrn,
          taskStatus,
          ignoreBackfillConditions,
        });
      },
    );

  const commitTaxonomyMutation = useMutation<void, Error, { taxonomyId: string }>({
    mutationFn: async ({ taxonomyId }) => {
      await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/commit`, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const recallTaxonomyMutation = useMutation<void, Error, { taxonomyId: string }>({
    mutationFn: async ({ taxonomyId }) => {
      await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/recall`, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const updateTaxonomyDescriptionMutation = useMutation<
    void,
    Error,
    { taxonomyId: string; description: string }
  >({
    mutationFn: async ({ taxonomyId, description }) => {
      await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/update_description`, {
        method: 'POST',
        body: { description },
      });
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const updateTaxonomyRootNameMutation = useMutation<
    void,
    Error,
    { taxonomyId: string; rootName: string }
  >({
    mutationFn: async ({ taxonomyId, rootName }) => {
      await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/update_root_name`, {
        method: 'POST',
        body: { rootName },
      });
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const reviewTaxonomyProposalMutation = useMutation<
    AVTaxonomyDTO,
    Error,
    FrontendReviewTaxonomyProposalRequest
  >({
    mutationFn: async ({ taxonomyId, isCustomerProposal, isAccept }) => {
      return await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/review`, {
        method: 'POST',
        body: { isCustomerProposal, isAccept },
      });
    },
    onSuccess: () => {
      refetchTaxonomiesAndInstructions();
    },
  });

  const getAllTaxonomyInstructionsMutation = useMutation<
    TaxonomyInstructionsHistoryDTO,
    Error,
    { taxonomyId: string }
  >({
    mutationFn: async ({ taxonomyId }) => {
      return await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/instructions`, {
        method: 'GET',
      });
    },
  });

  const getTaxonomyByIdMutation = useMutation<AVTaxonomyDTO, Error, { taxonomyId: string }>({
    mutationFn: async ({ taxonomyId }) => {
      const taxonomy: AVTaxonomyDTO = await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}`, {
        method: 'GET',
      });
      return taxonomy;
    },
  });

  const getLatestTaxonomyInstructionsMutation = useMutation<
    AVInstructionsDTO | null,
    Error,
    { taxonomyId: string }
  >({
    mutationFn: async ({ taxonomyId }) => {
      return await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/instructions/draft`, {
        method: 'GET',
      });
    },
  });

  const getLatestPublishedInstructionsMutation = useMutation<
    AVInstructionsDTO | null,
    Error,
    { taxonomyId: string }
  >({
    mutationFn: async ({ taxonomyId }) => {
      return await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/instructions/published`, {
        method: 'GET',
      });
    },
  });

  const createNewTaxonomyInstructionsMutation = useMutation<
    AVInstructionsDTO,
    Error,
    { taxonomyId: string }
  >({
    mutationFn: async ({ taxonomyId }) => {
      return await taxonomyApiFetch(`internal/taxonomies/${taxonomyId}/instructions/create`, {
        method: 'POST',
      });
    },
  });

  const getInstructionsByIdMutation = useMutation<
    AVInstructionsDTO | null,
    Error,
    { instructionId: string }
  >({
    mutationFn: async ({ instructionId }) => {
      return await taxonomyApiFetch(`internal/instructions/${instructionId}`, {
        method: 'GET',
      });
    },
  });

  const saveDraftInstructionsMutation = useMutation<
    AVInstructionsDTO,
    Error,
    { instructionId: string; draft: ComposableInstruction }
  >({
    mutationFn: async ({ instructionId, draft }) => {
      return await taxonomyApiFetch(`internal/instructions/${instructionId}/draft`, {
        method: 'POST',
        body: draft,
      });
    },
  });

  const publishDraftInstructionsMutation = useMutation<
    AVInstructionsDTO,
    Error,
    { instructionId: string }
  >({
    mutationFn: async ({ instructionId }) => {
      return await taxonomyApiFetch(`internal/instructions/${instructionId}/publish`, {
        method: 'POST',
      });
    },
  });

  const getMasterTaxonomyMutation = useMutation<AVTaxonomyDTO | null, Error, { projectId: string }>(
    {
      mutationFn: async ({ projectId }) => {
        const taxonomy: AVTaxonomyDTO | null = await taxonomyApiFetch(
          `internal/taxonomies/master_taxonomy?projectId=${projectId}`,
          {
            method: 'GET',
          },
        );
        return taxonomy;
      },
    },
  );

  const useBackfillTasksWithInstructions = () =>
    useMutation<any, Error, BackfillTasksInstructionsRequestDTO>(
      async ({
        projectId,
        projectName,
        instructionsSrn,
        taskIds,
        batchId,
        childProjectId,
        taxonomyName,
        taxonomySrn,
        taskStatus,
      }) => {
        return await invokeAsyncCorpJob('backfillTasksInstructions', {
          projectId,
          projectName,
          instructionsSrn,
          taskIds,
          batchId,
          childProjectId,
          taxonomyName,
          taxonomySrn,
          taskStatus,
        });
      },
    );

  const {
    data: loadedTaxonomyCache,
    refetch: refetchTaxonomiesAndInstructions,
    isFetching: isLoadingTaxonomies,
  } = getTaxonomiesAndInstructionsQuery;

  const { mutate: createTaxonomy, isLoading: isCreatingTaxonomy } = createTaxonomyMutation;
  const { mutate: saveDraft, isLoading: isSavingDraft } = saveDraftTaxonomyMutation;
  const { mutate: saveProposal, isLoading: isSavingProposal } = saveProposalTaxonomyMutation;
  const { mutate: inheritTaxonomies, isLoading: isInheriting } = inheritTaxonomiesMutation;
  const { mutate: publishTaxonomy, isLoading: isPublishing } = publishTaxonomyMutation;
  const { mutate: requestFEApproval, isLoading: isRequestingApproval } = requestFEApprovalMutation;
  const { mutate: forkTaxonomy, isLoading: isForking } = forkTaxonomyMutation;
  const { mutate: commitTaxonomy, isLoading: isCommitting } = commitTaxonomyMutation;
  const { mutate: recallTaxonomy, isLoading: isRecalling } = recallTaxonomyMutation;
  const { isLoading: isSpawningBackfillJob, mutate: backfillTasksWithTaxonomy } =
    useBackfillTasksWithTaxonomy();
  const { mutate: reviewTaxonomyProposal, isLoading: isReviewingProposal } =
    reviewTaxonomyProposalMutation;
  const { mutate: updateTaxonomyDescription, isLoading: isUpdatingDescription } =
    updateTaxonomyDescriptionMutation;
  const { mutate: updateTaxonomyRootName, isLoading: isUpdatingRootName } =
    updateTaxonomyRootNameMutation;
  const { mutate: getAllTaxonomyInstructions, isLoading: isGettingAllTaxonomyInstructions } =
    getAllTaxonomyInstructionsMutation;
  const { mutate: getTaxonomyById, isLoading: isGettingTaxonomyById } = getTaxonomyByIdMutation;
  const { mutate: getLatestTaxonomyInstructions } = getLatestTaxonomyInstructionsMutation;
  const { mutate: getLatestPublishedInstructions } = getLatestPublishedInstructionsMutation;
  const { mutate: createNewTaxonomyInstructions, isLoading: isCreatingInstructions } =
    createNewTaxonomyInstructionsMutation;
  const { mutate: getInstructionsById } = getInstructionsByIdMutation;
  const { mutate: saveDraftInstructions, isLoading: isSavingDraftInstructions } =
    saveDraftInstructionsMutation;
  const { mutate: publishDraftInstructions, isLoading: isPublishingInstructions } =
    publishDraftInstructionsMutation;
  const { isLoading: isBackfillingInstructions, mutate: backfillTasksWithInstructions } =
    useBackfillTasksWithInstructions();
  const { mutate: getMasterTaxonomy, isLoading: isGettingMasterTaxonomy } =
    getMasterTaxonomyMutation;
  return {
    refetchTaxonomiesAndInstructions,
    loadedTaxonomyCache,
    isLoadingTaxonomies,
    createTaxonomy,
    isCreatingTaxonomy,
    saveDraft,
    isSavingDraft,
    saveProposal,
    isSavingProposal,
    inheritTaxonomies,
    isInheriting,
    publishTaxonomy,
    isPublishing,
    requestFEApproval,
    isRequestingApproval,
    forkTaxonomy,
    isForking,
    commitTaxonomy,
    isCommitting,
    recallTaxonomy,
    isRecalling,
    isSpawningBackfillJob,
    backfillTasksWithTaxonomy,
    updateTaxonomyDescription,
    isUpdatingDescription,
    updateTaxonomyRootName,
    isUpdatingRootName,
    reviewTaxonomyProposal,
    isReviewingProposal,
    getAllTaxonomyInstructions,
    isGettingAllTaxonomyInstructions,
    getTaxonomyById,
    isGettingTaxonomyById,
    getLatestTaxonomyInstructions,
    getLatestPublishedInstructions,
    createNewTaxonomyInstructions,
    getInstructionsById,
    isCreatingInstructions,
    saveDraftInstructions,
    isSavingDraftInstructions,
    publishDraftInstructions,
    isPublishingInstructions,
    backfillTasksWithInstructions,
    isBackfillingInstructions,
    getMasterTaxonomy,
    isGettingMasterTaxonomy,
  };
}
